import { useState } from "react";
import Button from "./ui/Button";
import Dialog from "./ui/Dialog";

interface ProjectImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (json: string) => void;
}

export default function ProjectImportModal({
  isOpen,
  onClose,
  onImport,
}: ProjectImportModalProps) {
  const [json, setJson] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    setError(null);

    if (!json.trim()) {
      setError("Please paste project JSON");
      return;
    }

    try {
      // Validate JSON
      JSON.parse(json);
      onImport(json);
      setJson("");
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid JSON format");
    }
  };

  const handleClose = () => {
    setJson("");
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Content>
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-800">
          <Dialog.Title>Import Project</Dialog.Title>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 md:p-6 overflow-y-auto space-y-3 md:space-y-4">
          <Dialog.Description>
            Paste the project JSON below to restore device settings, patterns, and CCs.
          </Dialog.Description>

          <textarea
            value={json}
            onChange={(e) => setJson(e.target.value)}
            placeholder='Paste project JSON here...'
            className="w-full h-48 md:h-64 p-3 md:p-4 bg-gray-950 border-2 border-gray-800 rounded font-mono text-xs md:text-sm text-gray-300 focus:border-purple-700 focus:outline-none resize-none"
          />

          {error && (
            <div className="bg-red-900/30 border-2 border-red-500 rounded p-3 md:p-4 text-red-300 text-xs md:text-sm">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="text-xs text-gray-500 space-y-1">
            <p><strong>Note:</strong> This will update all matching devices with the saved state.</p>
            <p>Hardware devices are matched by unique ID. Virtual synths are matched by type.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 md:p-6 border-t border-gray-800">
          <Button onClick={handleClose} variant="secondary" className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="primary" className="w-full sm:w-auto">
            Import Project
          </Button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
